import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, FolderInput, Search, ArrowRight, Info } from "lucide-react";
import api from "@/lib/api";

const DEFAULT_BANNER = "/depots-banner.png";

export default function DepotsHome() {
  const [year, setYear] = useState(null);
  const [banner, setBanner] = useState(DEFAULT_BANNER);
  useEffect(() => {
    api.get("/academic-years/active").then((r) => setYear(r.data?.label)).catch(() => {});
    api.get("/content/platform.depots").then((r) => {
      if (r.data?.banner_image) setBanner(r.data.banner_image);
    }).catch(() => {});
  }, []);

  return (
    <div>
      <section className="relative h-[46vh] min-h-[340px] overflow-hidden">
        <img
          src={banner}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-veil" />
        <div className="relative h-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-14">
          <div className="eyebrow" style={{ color: "#E5C158" }}>Plateforme</div>
          <h1 className="mt-3 text-white text-4xl sm:text-5xl font-bold tracking-tight">
            Dépôts des travaux académiques
          </h1>
          {year && (
            <div className="mt-4 inline-flex items-center gap-2 bg-white/10 text-white px-3 py-1.5 rounded-sm text-sm w-fit backdrop-blur">
              Année académique active : <span className="text-unisic-gold font-semibold">{year}</span>
            </div>
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-unisic-gold" />
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="eyebrow eyebrow-dark">Présentation</div>
              <span className="divider-gold mt-3" />
              <p className="mt-6 text-slate-700 leading-relaxed">
                Cette plateforme est réservée aux étudiants inscrits à l'UNISIC pour la soumission
                officielle de leur mémoire ou de leur projet tutoré. Chaque dépôt reçoit un numéro
                unique, un accusé de réception et un suivi complet jusqu'à la validation finale.
              </p>
            </div>

            <div className="paper-card p-6 border-l-4 border-l-unisic-gold flex gap-4">
              <Info className="w-5 h-5 text-unisic-gold flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-700 space-y-2">
                <p className="font-semibold text-unisic-blue">Instructions importantes</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    <strong>Un code d'accès est obligatoire.</strong> Il vous a été remis par le
                    Secrétariat Général à la Recherche ; sans ce code, le dépôt ne peut pas être
                    effectué.
                  </li>
                  <li>Le dépôt doit être effectué avant la date limite fixée par la faculté.</li>
                  <li>Le fichier PDF principal ne doit pas dépasser 60 Mo.</li>
                  <li>Les informations du directeur (nom et e-mail) doivent être exactes.</li>
                  <li>Un accusé de réception est envoyé automatiquement après validation du formulaire.</li>
                </ul>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <Link
                to="/plateformes/depots/memoire"
                className="paper-card card-hover p-8 flex flex-col justify-between group"
                data-testid="btn-depot-memoire"
              >
                <div>
                  <div className="w-12 h-12 rounded-sm bg-unisic-blue/10 text-unisic-blue flex items-center justify-center">
                    <FileText className="w-6 h-6" />
                  </div>
                  <h3 className="mt-5 font-display font-semibold text-unisic-blue text-xl">
                    Déposer un mémoire
                  </h3>
                  <p className="mt-3 text-sm text-slate-600">
                    Formulaire complet pour le dépôt d'un mémoire de fin de cycle.
                  </p>
                </div>
                <div className="mt-6 inline-flex items-center gap-2 font-semibold text-unisic-red group-hover:gap-3 transition-all">
                  Commencer <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
              <Link
                to="/plateformes/depots/projet-tutore"
                className="paper-card card-hover p-8 flex flex-col justify-between group"
                data-testid="btn-depot-projet"
              >
                <div>
                  <div className="w-12 h-12 rounded-sm bg-unisic-gold/15 text-unisic-gold flex items-center justify-center">
                    <FolderInput className="w-6 h-6" />
                  </div>
                  <h3 className="mt-5 font-display font-semibold text-unisic-blue text-xl">
                    Déposer un projet tutoré
                  </h3>
                  <p className="mt-3 text-sm text-slate-600">
                    Formulaire dédié aux projets tutorés menés en équipe.
                  </p>
                </div>
                <div className="mt-6 inline-flex items-center gap-2 font-semibold text-unisic-red group-hover:gap-3 transition-all">
                  Commencer <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="paper-card p-6 bg-unisic-blue text-white">
              <div className="eyebrow" style={{ color: "#E5C158" }}>Suivi</div>
              <h3 className="mt-2 text-lg font-display font-semibold">Suivre mon dossier</h3>
              <p className="mt-2 text-sm text-white/80">
                Entrez votre numéro de suivi pour consulter l'état d'avancement de votre dépôt.
              </p>
              <Link
                to="/suivi-depot"
                className="mt-5 flex items-center justify-between px-4 py-3 border border-white/25 hover:border-unisic-gold rounded-sm text-sm"
                data-testid="link-track"
              >
                <span className="flex items-center gap-2">
                  <Search className="w-4 h-4" /> Accéder au suivi
                </span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="paper-card p-6">
              <div className="eyebrow eyebrow-dark">Format du numéro</div>
              <p className="mt-3 text-sm text-slate-600">
                Chaque dépôt reçoit un numéro unique généré automatiquement.
              </p>
              <div className="mt-4 bg-slate-50 border border-slate-200 rounded-sm px-3 py-3 text-sm font-mono text-unisic-blue">
                UNISIC-MEM-2025-2026-000001
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
